import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FiStar, FiClock, FiBriefcase, FiCheckCircle, FiUser, FiCalendar, FiMessageSquare, FiAward, FiBookOpen, FiX, FiVideo, FiPhone, FiMapPin, FiShield } from 'react-icons/fi';

const API_URL = `${process.env.REACT_APP_API_URL}/api/lawyers`;

function LawyerProfileModern() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLawyer, setSelectedLawyer] = useState(null);

  useEffect(() => {
    fetchLawyerDetails();
  }, [id]);

  const fetchLawyerDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/edit.php?id=${id}`);
      if (response.data.success) {
        const lawyer = response.data.data;
        setSelectedLawyer({
          id: lawyer.id,
          full_name: lawyer.full_name,
          profile_image: lawyer.profile_image,
          specialty: lawyer.specialty,
          avatar: '👨‍⚖️',
          rating: (lawyer.successRate || 80) / 20,
          reviews: Math.floor((lawyer.cases || 0) * 0.7),
          verified: lawyer.verified,
          bio: `Experienced ${lawyer.specialty.toLowerCase()} attorney with ${lawyer.experience_years || 0} years of professional practice and expertise in legal matters.`,
          experience: lawyer.experience_years || 0,
          cases: lawyer.cases || 0,
          successRate: lawyer.successRate || 80,
          rate: lawyer.hourly_rate || 150,
          responseTime: '< 2 hours',
          education: [
            { school: 'Law School', degree: 'Juris Doctor', year: 2015 },
            { school: 'University', degree: 'B.A.', year: 2012 }
          ],
          licenses: [
            lawyer.license_number ? `License #${lawyer.license_number}` : 'License Pending',
            lawyer.bar_association || 'State Bar Association',
            'Certified Professional',
            'Member, Legal Association'
          ],
          expertise: [
            { icon: <FiBriefcase />, name: lawyer.specialty },
            { icon: <FiMessageSquare />, name: 'Legal Consultation' },
            { icon: <FiShield />, name: 'Negotiation' },
            { icon: <FiBookOpen />, name: 'Documentation' },
            { icon: <FiStar />, name: 'Case Analysis' },
            { icon: <FiCheckCircle />, name: 'Legal Advice' }
          ],
          reviews_list: [
            { name: 'Client One', rating: 5, date: '2 weeks ago', text: 'Professional and knowledgeable attorney. Excellent service and outcomes.' },
            { name: 'Client Two', rating: 5, date: '1 month ago', text: 'Great communication and legal expertise. Highly recommended.' },
            { name: 'Client Three', rating: 5, date: '2 months ago', text: 'Outstanding representation and attention to detail.' }
          ]
        });
        setError(null);
      } else {
        setError('Lawyer not found');
      }
    } catch (err) {
      console.error('Error fetching lawyer details:', err);
      setError('Failed to load lawyer profile');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = () => { navigate(`/booking?lawyer_id=${id}`); };

  const handleSendMessage = () => {
    if (message.trim()) {
      alert('Message sent successfully!');
      setMessage('');
      setShowMessageModal(false);
    }
  };

  const tabs = [
    { key: 'overview', label: 'Overview', icon: <FiUser className="w-4 h-4" /> },
    { key: 'experience', label: 'Qualifications', icon: <FiAward className="w-4 h-4" /> },
    { key: 'reviews', label: 'Reviews', icon: <FiStar className="w-4 h-4" /> },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50">
        <div className="h-72" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 100%)' }} />
        <div className="max-w-6xl mx-auto px-4 -mt-20">
          <div className="animate-pulse bg-white rounded-2xl p-8">
            <div className="flex items-center gap-6">
              <div className="w-28 h-28 rounded-2xl bg-secondary-200" />
              <div className="space-y-3 flex-1"><div className="h-6 bg-secondary-200 rounded w-48" /><div className="h-4 bg-secondary-200 rounded w-32" /></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !selectedLawyer) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-danger-50 flex items-center justify-center mx-auto mb-4">
            <FiX className="w-8 h-8 text-danger-500" />
          </div>
          <p className="text-danger-600 text-lg font-medium mb-4">{error || 'Lawyer not found'}</p>
          <button onClick={() => navigate('/lawyers')} className="btn-primary">Back to Lawyers</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
        </div>
        <div className="max-w-6xl mx-auto px-4 pt-12 pb-32" />
      </div>

      {/* Profile Card */}
      <div className="max-w-6xl mx-auto px-4 -mt-24 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Avatar */}
            <div className="relative">
              <div className="w-28 h-28 rounded-2xl overflow-hidden bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center flex-shrink-0 ring-4 ring-white shadow-lg">
                {selectedLawyer.profile_image ? (
                  <img src={`${process.env.REACT_APP_API_URL}${selectedLawyer.profile_image}`} alt={selectedLawyer.full_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-5xl">{selectedLawyer.avatar}</span>
                )}
              </div>
              {selectedLawyer.verified && (
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center ring-3 ring-white">
                  <FiCheckCircle className="w-4 h-4 text-white" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                {selectedLawyer.verified && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-600">
                    <FiCheckCircle className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-1">{selectedLawyer.full_name}</h1>
              <p className="text-primary-500 font-medium mb-4">{selectedLawyer.specialty}</p>

              {/* Stats Row */}
              <div className="flex flex-wrap gap-6">
                {[
                  { icon: <FiStar className="w-4 h-4 text-accent-500" />, value: `${selectedLawyer.rating}★`, label: `${selectedLawyer.reviews} Reviews` },
                  { icon: <FiClock className="w-4 h-4 text-primary-400" />, value: `${selectedLawyer.experience}+`, label: 'Years Exp.' },
                  { icon: <FiCheckCircle className="w-4 h-4 text-teal-500" />, value: `${selectedLawyer.successRate}%`, label: 'Success' },
                  { icon: <span className="text-sm font-bold text-secondary-400">₹</span>, value: selectedLawyer.rate, label: '/hour' },
                ].map((stat, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {stat.icon}
                    <div>
                      <span className="font-bold text-secondary-900">{stat.value}</span>
                      <span className="text-secondary-400 text-xs ml-1">{stat.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 w-full md:w-auto">
              <button onClick={handleBooking} className="btn-primary py-3 px-6 gap-2">
                <FiCalendar className="w-4 h-4" /> Book Consultation
              </button>
              <button onClick={() => setShowMessageModal(true)} className="btn-ghost py-3 px-6 border border-secondary-200 gap-2">
                <FiMessageSquare className="w-4 h-4" /> Send Message
              </button>
            </div>
          </div>
        </motion.div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-16">
          {/* Main Column */}
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 overflow-hidden">
              <div className="flex border-b border-secondary-100">
                {tabs.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex-1 flex items-center justify-center gap-2 py-4 px-4 text-sm font-medium transition-all relative ${
                      activeTab === tab.key
                        ? 'text-primary-600'
                        : 'text-secondary-400 hover:text-secondary-600'
                    }`}
                  >
                    {tab.icon} {tab.label}
                    {activeTab === tab.key && (
                      <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" />
                    )}
                  </button>
                ))}
              </div>

              <div className="p-6 md:p-8">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                    <section>
                      <h2 className="text-lg font-semibold text-secondary-900 mb-3 flex items-center gap-2">
                        <FiUser className="w-5 h-5 text-primary-400" /> About {selectedLawyer.full_name}
                      </h2>
                      <p className="text-secondary-600 leading-relaxed mb-3">{selectedLawyer.bio}</p>
                      <p className="text-secondary-600 leading-relaxed">
                        Throughout my career, I have successfully defended over {selectedLawyer.cases} cases with a strong track record of favorable outcomes. I believe in personalized legal representation and work closely with each client to understand their unique situation and objectives.
                      </p>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                        <FiAward className="w-5 h-5 text-primary-400" /> Areas of Expertise
                      </h2>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {selectedLawyer.expertise.map((exp, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-secondary-50 border border-secondary-100 hover:border-primary-200 hover:bg-primary-50/50 transition-colors">
                            <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center text-primary-500 flex-shrink-0">{exp.icon}</div>
                            <span className="text-sm font-medium text-secondary-700">{exp.name}</span>
                          </div>
                        ))}
                      </div>
                    </section>
                  </motion.div>
                )}

                {/* Experience Tab */}
                {activeTab === 'experience' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                    <section>
                      <h2 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                        <FiBookOpen className="w-5 h-5 text-primary-400" /> Education
                      </h2>
                      <div className="space-y-4 relative before:absolute before:left-4 before:top-2 before:bottom-2 before:w-px before:bg-secondary-200">
                        {selectedLawyer.education.map((edu, idx) => (
                          <div key={idx} className="flex gap-4 pl-4 relative">
                            <div className="w-3 h-3 rounded-full bg-primary-400 border-2 border-white ring-2 ring-primary-100 mt-1.5 flex-shrink-0 relative z-10" />
                            <div className="bg-secondary-50 rounded-xl p-4 flex-1">
                              <h5 className="font-semibold text-secondary-900">{edu.school}</h5>
                              <p className="text-sm text-secondary-500">{edu.degree}</p>
                              <span className="text-xs text-secondary-400">{edu.year}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section>
                      <h2 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                        <FiAward className="w-5 h-5 text-primary-400" /> Licenses & Certifications
                      </h2>
                      <div className="space-y-2">
                        {selectedLawyer.licenses.map((license, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-teal-50/50 border border-teal-100">
                            <FiCheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                            <span className="text-sm text-secondary-700">{license}</span>
                          </div>
                        ))}
                      </div>
                    </section>
                  </motion.div>
                )}

                {/* Reviews Tab */}
                {activeTab === 'reviews' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <h2 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                      <FiStar className="w-5 h-5 text-accent-500" /> Client Reviews ({selectedLawyer.reviews})
                    </h2>
                    <div className="space-y-4">
                      {selectedLawyer.reviews_list.map((review, idx) => (
                        <div key={idx} className="p-5 rounded-xl bg-secondary-50 border border-secondary-100">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                                <FiUser className="w-4 h-4 text-primary-400" />
                              </div>
                              <div>
                                <span className="font-medium text-secondary-900 text-sm">{review.name}</span>
                                <p className="text-xs text-secondary-400">{review.date}</p>
                              </div>
                            </div>
                            <div className="flex gap-0.5">
                              {[...Array(5)].map((_, i) => (
                                <FiStar key={i} className={`w-3.5 h-3.5 ${i < review.rating ? 'fill-accent-500 text-accent-500' : 'text-secondary-300'}`} />
                              ))}
                            </div>
                          </div>
                          <p className="text-secondary-600 text-sm leading-relaxed">{review.text}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">Quick Actions</h3>
              <button onClick={handleBooking} className="w-full btn-primary py-3 gap-2 mb-3">
                <FiCalendar className="w-4 h-4" /> Book Now
              </button>
              <button onClick={() => setShowMessageModal(true)} className="w-full btn-ghost py-3 border border-secondary-200 gap-2">
                <FiMessageSquare className="w-4 h-4" /> Message
              </button>
            </div>

            {/* Response Time */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
              <h3 className="font-semibold text-secondary-900 mb-2">Response Time</h3>
              <p className="text-2xl font-bold text-primary-500 mb-1">{selectedLawyer.responseTime}</p>
              <p className="text-xs text-secondary-400">Typically responds within this timeframe</p>
            </div>

            {/* Availability */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">Availability</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-secondary-600">Mon – Fri</span>
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-teal-50 text-teal-600">Available</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-secondary-600">Sat – Sun</span>
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-500">Call to confirm</span>
                </div>
              </div>
            </div>

            {/* Consultation Types */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">Consultation Types</h3>
              <div className="space-y-3">
                {[
                  { icon: <FiVideo className="w-4 h-4 text-primary-400" />, name: 'Video Call', rate: '₹2,500/hour' },
                  { icon: <FiPhone className="w-4 h-4 text-primary-400" />, name: 'Phone Call', rate: '₹2,000/hour' },
                  { icon: <FiMapPin className="w-4 h-4 text-primary-400" />, name: 'In-Person', rate: '₹3,500/hour' },
                ].map((type, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-secondary-50 border border-secondary-100">
                    <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">{type.icon}</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-secondary-700">{type.name}</p>
                      <p className="text-xs text-secondary-400">{type.rate}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Verified Badge */}
            <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl border border-teal-200/50 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center mx-auto mb-3">
                <FiShield className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-secondary-900 mb-1">Verified Professional</h4>
              <p className="text-xs text-secondary-500">All credentials and background verified by Bihar Lawyer</p>
            </div>
          </div>
        </div>
      </div>

      {/* Message Modal */}
      <AnimatePresence>
        {showMessageModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowMessageModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b border-secondary-100">
                <h3 className="font-semibold text-secondary-900">Message {selectedLawyer.full_name}</h3>
                <button onClick={() => setShowMessageModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-secondary-100 transition-colors">
                  <FiX className="w-4 h-4 text-secondary-400" />
                </button>
              </div>
              <div className="p-6">
                <textarea
                  placeholder="Type your message here..."
                  rows="5"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="textarea"
                />
              </div>
              <div className="flex gap-3 p-6 pt-0">
                <button onClick={() => setShowMessageModal(false)} className="flex-1 btn-ghost py-3 border border-secondary-200">Cancel</button>
                <button onClick={handleSendMessage} className="flex-1 btn-primary py-3">Send Message</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LawyerProfileModern;
